import type { MiaChatMessage, MiaPublicConfig, MiaWidgetState } from '../types';
import { escapeHtml } from '../utils';
import { widgetStyles } from './styles';

export type WidgetUIHandlers = {
  onToggle: () => void;
  onClose: () => void;
  onSend: (text: string) => void;
};

export class WidgetUI {
  private host: HTMLElement | null = null;
  private shadow: ShadowRoot | null = null;
  private panelEl: HTMLElement | null = null;
  private messagesEl: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private inputEl: HTMLTextAreaElement | null = null;
  private sendBtn: HTMLButtonElement | null = null;
  private liveEl: HTMLElement | null = null;
  private mounted = false;

  mount(config: MiaPublicConfig, handlers: WidgetUIHandlers): void {
    if (this.mounted) return;

    this.host = document.createElement('div');
    this.host.id = 'mia-widget-host';
    this.host.setAttribute('data-mia-widget', '1');
    this.shadow = this.host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = widgetStyles(String(config.theme.primaryColor ?? '#0F766E'));
    this.shadow.appendChild(style);

    const root = document.createElement('div');
    root.className = `mia-root pos-${config.position === 'left' ? 'left' : 'right'}`;
    root.innerHTML = `
      <div class="mia-panel" role="dialog" aria-modal="false" aria-label="${escapeHtml(config.name)}" hidden>
        <header class="mia-header">
          ${config.logoUrl ? `<img class="mia-logo" alt="" width="28" height="28" />` : ''}
          <h2 class="mia-title"></h2>
          <button type="button" class="mia-icon-btn mia-close" aria-label="Cerrar chat">×</button>
        </header>
        <div class="mia-messages" role="log" aria-live="polite" aria-relevant="additions"></div>
        <div class="mia-status" aria-live="polite"></div>
        <div class="mia-sr-only mia-live" aria-live="assertive"></div>
        <form class="mia-composer">
          <label class="mia-sr-only" for="mia-input">Mensaje</label>
          <textarea id="mia-input" class="mia-input" rows="1" placeholder="Escribí tu mensaje…" autocomplete="off"></textarea>
          <button type="submit" class="mia-send">Enviar</button>
        </form>
      </div>
      <button type="button" class="mia-fab" aria-label="Abrir chat ${escapeHtml(config.name)}" aria-expanded="false">💬</button>
    `;

    this.shadow.appendChild(root);
    document.body.appendChild(this.host);

    this.panelEl = root.querySelector('.mia-panel');
    this.messagesEl = root.querySelector('.mia-messages');
    this.statusEl = root.querySelector('.mia-status');
    this.inputEl = root.querySelector('.mia-input');
    this.sendBtn = root.querySelector('.mia-send');
    this.liveEl = root.querySelector('.mia-live');

    const title = root.querySelector('.mia-title');
    if (title) title.textContent = config.name;

    const logo = root.querySelector('.mia-logo') as HTMLImageElement | null;
    if (logo && config.logoUrl && /^https?:\/\//i.test(config.logoUrl)) {
      logo.src = config.logoUrl;
    } else if (logo) {
      logo.remove();
    }

    const fab = root.querySelector('.mia-fab') as HTMLButtonElement;
    const closeBtn = root.querySelector('.mia-close') as HTMLButtonElement;
    const form = root.querySelector('.mia-composer') as HTMLFormElement;

    fab.addEventListener('click', () => handlers.onToggle());
    closeBtn.addEventListener('click', () => handlers.onClose());
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const text = this.inputEl?.value ?? '';
      handlers.onSend(text);
    });

    this.inputEl?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handlers.onSend(this.inputEl?.value ?? '');
      }
    });

    this.shadow.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'Escape') handlers.onClose();
    });

    this.mounted = true;
  }

  render(state: MiaWidgetState): void {
    if (!this.mounted || !this.panelEl || !this.messagesEl) return;

    const open = state.open;
    this.panelEl.classList.toggle('open', open);
    if (open) {
      this.panelEl.removeAttribute('hidden');
    } else {
      this.panelEl.setAttribute('hidden', '');
    }

    const fab = this.shadow?.querySelector('.mia-fab') as HTMLButtonElement | null;
    if (fab) fab.setAttribute('aria-expanded', open ? 'true' : 'false');

    this.messagesEl.innerHTML = '';
    for (const msg of state.messages) {
      this.messagesEl.appendChild(this.buildMessageNode(msg));
    }
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;

    if (this.statusEl) {
      this.statusEl.classList.toggle('error', !!state.error);
      if (state.error) {
        this.statusEl.textContent = state.error.message;
      } else if (state.sending || state.loading) {
        this.statusEl.textContent = 'Escribiendo…';
      } else {
        this.statusEl.textContent = '';
      }
    }

    if (this.sendBtn) this.sendBtn.disabled = state.sending || state.loading || !state.initialized;
    if (this.inputEl) this.inputEl.disabled = state.sending || !state.initialized;
  }

  focusInput(): void {
    this.inputEl?.focus();
  }

  clearInput(): void {
    if (this.inputEl) this.inputEl.value = '';
  }

  announce(text: string): void {
    if (this.liveEl) this.liveEl.textContent = text;
  }

  destroy(): void {
    this.host?.remove();
    this.host = null;
    this.shadow = null;
    this.panelEl = null;
    this.messagesEl = null;
    this.statusEl = null;
    this.inputEl = null;
    this.sendBtn = null;
    this.liveEl = null;
    this.mounted = false;
  }

  /**
   * Minimal non-chat UI when bootstrap fails before the main panel mounts.
   */
  mountBootstrapError(message: string, onRetry: () => void, position: 'left' | 'right' = 'right'): void {
    this.destroy();
    this.host = document.createElement('div');
    this.host.id = 'mia-widget-host';
    this.host.setAttribute('data-mia-widget', '1');
    this.host.setAttribute('data-mia-fallback', '1');
    this.shadow = this.host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = widgetStyles('#0F766E');
    this.shadow.appendChild(style);

    const root = document.createElement('div');
    root.className = `mia-root pos-${position === 'left' ? 'left' : 'right'}`;
    const box = document.createElement('div');
    box.className = 'mia-fallback';
    box.setAttribute('role', 'alert');
    const p = document.createElement('p');
    p.textContent = message;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mia-send';
    btn.textContent = 'Reintentar';
    btn.addEventListener('click', () => onRetry());
    box.appendChild(p);
    box.appendChild(btn);
    root.appendChild(box);
    this.shadow.appendChild(root);
    document.body.appendChild(this.host);
    this.mounted = true;
  }

  getShadowRoot(): ShadowRoot | null {
    return this.shadow;
  }

  private buildMessageNode(msg: MiaChatMessage): HTMLElement {
    const el = document.createElement('div');
    el.className = `mia-msg ${msg.role}`;
    el.setAttribute('data-role', msg.role);
    el.textContent = msg.content;
    return el;
  }
}
